import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { config } from "@/config";
import { getPostHogConfig, isPostHogEnabled } from "./posthogConfig";
import {
	registerPostHogSuperProperties,
	setPostHogInstance,
} from "./posthogEventEmitter";

interface PostHogContextValue {
	posthog: any;
	isInitialized: boolean;
}

const PostHogContext = createContext<PostHogContextValue>({
	posthog: null,
	isInitialized: false,
});

/**
 * PostHog Provider — lazy-loads posthog-js and initializes it once.
 * Wraps the app at the root layout level.
 */
export const PostHogProvider = ({ children }: { children: ReactNode }) => {
	const [posthog, setPosthog] = useState<any>(null);
	const [isInitialized, setIsInitialized] = useState(false);
	const initStartedRef = useRef(false);

	useEffect(() => {
		if (!isPostHogEnabled() || initStartedRef.current) return;
		initStartedRef.current = true;

		const posthogConfig = getPostHogConfig();

		const initPostHog = async () => {
			const posthogModule = await import("posthog-js");
			const posthogInstance = posthogModule.default;

			posthogInstance.init(posthogConfig.apiKey, {
				api_host: posthogConfig.host,
				defaults: "2026-05-30",
				autocapture: false,
				capture_pageview: true,
				capture_pageleave: true,
				capture_performance: false,
				capture_dead_clicks: false,
				rageclick: false,
				disable_session_recording: true,
				disable_surveys: true,
				enable_heatmaps: false,
				loaded: (ph: any) => {
					setPostHogInstance(ph);
					registerPostHogSuperProperties();
					// Only enable debug in local development.
					// PostHog's internal logger calls JSON.stringify on objects with
					// cyclic references, which throws on Mobile Safari / WKWebView.
					// The global safeJsonStringify patch (installed in main.tsx)
					// handles this transparently.
					if (config.global.env === "development") {
						ph.debug();
					}
				},
			});

			setPostHogInstance(posthogInstance);
			setPosthog(posthogInstance);
			setIsInitialized(true);
		};

		initPostHog();
	}, []);

	return (
		<PostHogContext.Provider value={{ posthog, isInitialized }}>
			{children}
		</PostHogContext.Provider>
	);
};

/**
 * Hook to access the PostHog instance
 */
export const usePostHog = () => useContext(PostHogContext).posthog;

/**
 * Hook to check if PostHog is initialized
 */
export const usePostHogInitialized = () =>
	useContext(PostHogContext).isInitialized;
